import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { formatPhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import DataSelect from '@components/fields/DataSelect';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import LocationAutocompleteInput from '@components/fields/LocationAutocompleteInput';
import PhoneInput from '@components/fields/PhoneInput';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import {
  ClientRoleRelationshipEnum,
  ClientRoleUpdateDocument,
  ClientRoleUpdateMutation,
  ClientRoleUpdateMutationVariables,
  ClientUpdateDocument,
  ClientUpdateMutation,
  ClientUpdateMutationVariables,
  GetClientWithAccountQuery,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForRelationship } from '@utils/optionUtils';

type ClientResponse = NonNullable<NonNullable<ClientUpdateMutation['clientUpdate']>['client']>;

type FormData = {
  email: string;
  firstName: string;
  lastName: string;
  number: string | null;
  placeId: string | null;
  relationshipOption: ClientRoleRelationshipEnum | null;
  relationshipOther: string | null;
  timezone: string | null;
};

type ClientRole = {
  id: string;
  relationship: {
    other: string | null;
    relationshipOption: ClientRoleRelationshipEnum | null;
  } | null;
};

type Props = {
  autofocus: boolean;
  careProfileName?: string | null;
  client: GetClientWithAccountQuery['client'] | GetJourneyWithCareProfileQuery['journey']['clientRoles'][0]['client'];
  clientRole?: ClientRole;
  onCancel: () => void;
  onError: () => void;
  onSuccess: (client: ClientResponse) => void;
};

const AccountForm = ({ autofocus, careProfileName, client, clientRole, onCancel, onError, onSuccess }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [clientUpdate] = useMutation<ClientUpdateMutation, ClientUpdateMutationVariables>(ClientUpdateDocument);

  const [updateClientRole] = useMutation<ClientRoleUpdateMutation, ClientRoleUpdateMutationVariables>(
    ClientRoleUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      email: yup
        .string()
        .email(intl.formatMessage({ id: 'errors.registration.email.notAnEmail' }))
        .required(intl.formatMessage({ id: 'errors.registration.email.blank' })),
      firstName: yup.string().required(intl.formatMessage({ id: 'errors.registration.firstName.blank' })),
      lastName: yup.string().required(intl.formatMessage({ id: 'errors.registration.lastName.blank' })),
      number: yup.string().nullable().defined(),
      placeId: yup.string().required(intl.formatMessage({ id: 'errors.careProfile.placeId.blank' })),
      relationshipOption: yup.string().oneOf(Object.values(ClientRoleRelationshipEnum)).nullable().defined(),
      relationshipOther: yup.string().nullable().defined(),
      timezone: yup.string().required(intl.formatMessage({ id: 'errors.registration.timezone.blank' }))
    })
    .required();

  const defaultValues: FormData = {
    email: client.email.email,
    firstName: client.firstName,
    lastName: client.lastName,
    number: client.consultPhoneNumber?.number ?? null,
    placeId: import.meta.env.VITE_NO_GOOGLE_LOCATION_CALLS ? 'ChIJfX6-HwSbf4gR4Aa9pkeWBLY' : client.location.placeId,
    relationshipOption: clientRole?.relationship?.relationshipOption ?? null,
    relationshipOther: clientRole?.relationship?.other ?? null,
    timezone: client.timezone
  };

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const number = form.watch('number');
  const relationshipOption = form.watch('relationshipOption');

  const relationshipOptions = optionsForRelationship(intl, 'models.clientRole.relationship');
  const relationshipOther = form.watch('relationshipOther');

  const isValid = useMemo(() => {
    if (number && number !== '') {
      return isValidPhoneNumber(number);
    } else {
      return false;
    }
  }, [number]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (
      clientRole &&
      careProfileName &&
      (relationshipOption !== clientRole.relationship?.relationshipOption ||
        relationshipOther !== clientRole.relationship?.other)
    ) {
      await updateClientRole({
        variables: {
          id: clientRole.id,
          relationship: {
            other: relationshipOption === ClientRoleRelationshipEnum.other ? form.getValues().relationshipOther : null,
            relationshipOption
          }
        }
      });
    }

    return clientUpdate({
      variables: {
        ...form.getValues(),
        formattedNumber: number ? formatPhoneNumber(number) : undefined,
        id: client.id,
        number
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.clientUpdate,
        intl,
        onError,
        onSuccess: () => {
          if (response.data?.clientUpdate?.client) {
            form.reset();
            onSuccess(response.data?.clientUpdate?.client);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.clientAccountUpdate'
      });
    });
  }, [
    careProfileName,
    client.id,
    clientRole,
    clientUpdate,
    form,
    intl,
    number,
    onError,
    onSuccess,
    relationshipOption,
    relationshipOther,
    updateClientRole
  ]);

  return (
    <Form data-test="account-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      {clientRole && careProfileName && (
        <div>
          <Select<FormData>
            control={form.control}
            error={form.formState.errors.relationshipOption}
            label={intl.formatMessage({ id: 'forms.relationship.label' }, { firstName: careProfileName })}
            name="relationshipOption"
            options={relationshipOptions}
          />
          <div className={relationshipOption !== ClientRoleRelationshipEnum.other ? 'ion-hide' : undefined}>
            <Input
              control={form.control}
              error={form.formState.errors.relationshipOther}
              label={intl.formatMessage({ id: 'forms.relationship.relationshipOtherLabel' })}
              name="relationshipOther"
            />
          </div>
        </div>
      )}
      <Input<FormData>
        autofocus={autofocus}
        control={form.control}
        data-test="email-input"
        error={form.formState.errors.email}
        label={intl.formatMessage({ id: 'forms.clientAccountUpdate.emailLabel' })}
        name="email"
        required
        type="email"
      />
      <PhoneInput<FormData>
        autofocus
        control={form.control}
        error={form.formState.errors.number}
        label={intl.formatMessage({ id: 'forms.clientAccountUpdate.numberLabel' })}
        name="number"
      />

      <Input<FormData>
        control={form.control}
        error={form.formState.errors.firstName}
        label={intl.formatMessage({ id: 'forms.clientAccountUpdate.firstNameLabel' })}
        name="firstName"
        required
      />
      <Input<FormData>
        control={form.control}
        error={form.formState.errors.lastName}
        label={intl.formatMessage({ id: 'forms.clientAccountUpdate.lastNameLabel' })}
        name="lastName"
        required
      />
      <DataSelect<FormData>
        control={form.control}
        error={form.formState.errors.timezone}
        label={intl.formatMessage({ id: 'forms.clientAccountUpdate.timezoneLabel' })}
        name="timezone"
        required
        type="timezones"
      />
      <LocationAutocompleteInput
        control={form.control}
        error={form.formState.errors.placeId}
        label={intl.formatMessage({ id: 'forms.careProfile.clientLocationLabel' })}
        location={{ address: client.location.address, placeId: client.location.placeId }}
        name="placeId"
        noResults={intl.formatMessage({ id: 'errors.careProfile.placeId.noResults' })}
        placeholder={intl.formatMessage({ id: 'forms.careProfile.clientLocationPlaceholder' })}
        required
        subLabel={intl.formatMessage({ id: 'forms.careProfile.locationSublabel' })}
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveDisabled={!!(!isValid && number)} />
    </Form>
  );
};

export default AccountForm;
